import { Box, Container, Flex, Heading, HStack, Image, Stack, Text } from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { CtaButton } from '../components/CtaButton';

// Redesign: a single soft violet bloom centred lower-right (Figma: a 1051px
// #270048 ellipse at ~0.42 effective alpha with a 64px blur). Reproduced as a
// wide radial gradient with a flat core so it bleeds visibly around the cards.
const SECTION_GLOW =
  'radial-gradient(56% 66% at 77% 68%, rgba(68,14,110,0.6) 0%, rgba(46,6,82,0.38) 46%, transparent 84%)';
// Subtle film-grain overlay (asset-free SVG noise) over the dark section.
const GRAIN =
  "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='140' height='140'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E\")";
// Mockup-panel background, layered top→bottom:
//  1. the Figma "wavy gradient" line texture (transparent PNG),
//  2. the panel gradient exported straight from Figma as a PNG — its gradient
//     handles extend beyond the rect, so a plain CSS 2-stop gradient renders too
//     flat; the PNG reproduces the real light→peach transition exactly,
//  3. a CSS gradient fallback for the (rare) case the PNG fails to load.
// All stretched to fill; applied via the raw `style` prop because Chakra's
// backgroundImage shorthand mangles a mixed `url(), linear-gradient()` value.
const PANEL = 'linear-gradient(145deg, #fbf5ef 6%, #fbe5d8 88%)';
const PANEL_WAVE = '/images/landing/feat-panel-wave.png';
const PANEL_GRAD = '/images/landing/feat-panel-gradient.png';
const PANEL_BG = {
  backgroundImage: `url(${PANEL_WAVE}), url(${PANEL_GRAD}), ${PANEL}`,
  backgroundSize: '100% 100%',
  backgroundRepeat: 'no-repeat',
};

const CARD = {
  bg: 'white',
  borderRadius: '20px',
  overflow: 'hidden',
} as const;

function CardText({ title, body }: { title: string; body: string }) {
  const points = body
    .split('. ')
    .map((s) => s.replace(/\.$/, '').trim())
    .filter(Boolean);
  return (
    <Stack spacing="14px">
      <Text fontSize="xl" fontWeight={600} color="onboarding.deepPlum">
        {title}
      </Text>
      <Stack spacing="8px">
        {points.map((p) => (
          <HStack key={p} align="flex-start" spacing="10px">
            <Box mt="8px" w="5px" h="5px" borderRadius="full" bg="onboarding.mutedRose" flexShrink={0} />
            <Text fontSize="sm" color="onboarding.roseMid" lineHeight={1.45}>
              {p}
            </Text>
          </HStack>
        ))}
      </Stack>
    </Stack>
  );
}

export function Features() {
  const { t } = useTranslation();
  const i = (k: string) => t(`landing.features.items.${k}`);

  return (
    <Box as="section" id="features" position="relative" overflow="hidden" bg="#270D17" color="white">
      <Box position="absolute" inset={0} backgroundImage={SECTION_GLOW} pointerEvents="none" />
      <Box
        position="absolute"
        inset={0}
        backgroundImage={GRAIN}
        opacity={0.05}
        mixBlendMode="overlay"
        pointerEvents="none"
      />
      <Container maxW="1240px" py={{ base: 16, lg: 24 }} position="relative">
        <Heading
          as="h2"
          fontFamily="heading"
          fontWeight={700}
          fontSize="display.lg"
          lineHeight={1.1}
          letterSpacing="-0.01em"
          color="white"
          mb={{ base: 8, lg: 12 }}
        >
          {t('landing.features.title')}
        </Heading>

        <Flex direction={{ base: 'column', lg: 'row' }} gap="20px" align="stretch">
          {/* Left column: Music (tall) + Announcements/Multi-location row */}
          <Flex direction="column" gap="20px" flex="1" minW={0}>
            <Flex {...CARD} direction="column" minH={{ lg: '515px' }} flex="1">
              <Stack p="40px 40px 0" spacing="14px">
                <CardText title={i('music.title')} body={i('music.body')} />
              </Stack>
              <Flex mt="20px" flex="1" style={PANEL_BG} minH="250px" overflow="hidden" position="relative">
                <Image
                  src="/images/landing/feat-music.png"
                  alt=""
                  position="absolute"
                  left="-23%"
                  top="2%"
                  w="121%"
                  maxW="none"
                  draggable={false}
                />
              </Flex>
            </Flex>

            <Flex direction={{ base: 'column', sm: 'row' }} gap="20px">
              <Box {...CARD} p="28px" flex="1" minH={{ lg: '309px' }}>
                <CardText title={i('announcements.title')} body={i('announcements.body')} />
              </Box>
              <Box {...CARD} p="28px" flex="1" minH={{ lg: '309px' }}>
                <CardText title={i('multiLocation.title')} body={i('multiLocation.body')} />
              </Box>
            </Flex>
          </Flex>

          {/* Right column: Scheduling (short) + Advertising (tall) */}
          <Flex direction="column" gap="20px" flex="1" minW={0}>
            <Flex {...CARD} minH={{ lg: '309px' }}>
              <Box
                w="49%"
                style={PANEL_BG}
                flexShrink={0}
                overflow="hidden"
                display={{ base: 'none', sm: 'block' }}
                position="relative"
              >
                <Image
                  src="/images/landing/feat-scheduling.png"
                  alt=""
                  position="absolute"
                  h="178%"
                  top="-30%"
                  left="-14%"
                  transform="rotate(-18.9deg)"
                  maxW="none"
                  draggable={false}
                />
              </Box>
              <Box p="28px" flex="1">
                <CardText title={i('scheduling.title')} body={i('scheduling.body')} />
              </Box>
            </Flex>

            <Flex {...CARD} direction="column" minH={{ lg: '515px' }} flex="1">
              <Stack p="40px 40px 0" spacing="14px">
                <CardText title={i('advertising.title')} body={i('advertising.body')} />
                <Box>
                  <CtaButton
                    to="/onboarding"
                    bg="onboarding.plum"
                    color="white"
                    borderRadius="pill"
                    height="48px"
                    px="28px"
                    fontSize="md"
                    fontWeight={600}
                    _hover={{ bg: 'onboarding.roseDeep' }}
                    withArrow
                    trackEventName="cta_click"
                    trackProps={{ section: 'features' }}
                  >
                    {t('landing.features.cta')}
                  </CtaButton>
                </Box>
              </Stack>
              <Box mt="20px" flex="1" style={PANEL_BG} minH="210px" position="relative" overflow="hidden">
                <Image
                  src="/images/landing/feat-ad.png"
                  alt=""
                  position="absolute"
                  w="150%"
                  left="-18%"
                  top="14%"
                  transform="rotate(-10.4deg)"
                  maxW="none"
                  draggable={false}
                />
              </Box>
            </Flex>
          </Flex>
        </Flex>
      </Container>
    </Box>
  );
}
